import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../utils/cn';

interface ThemeToggleProps {
  className?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        'relative inline-flex items-center',
        'w-12 h-6 p-0.5 rounded-full',
        'bg-gray-200 dark:bg-gray-700',
        'transition-colors duration-300',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        'focus:ring-gray-400 dark:focus:ring-gray-500',
        'focus:ring-offset-white dark:focus:ring-offset-gray-900',
        className
      )}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      role="switch"
      aria-checked={isDark}
    >
      {/* Sliding knob */}
      <span
        className={cn(
          'absolute w-5 h-5 rounded-full',
          'bg-white shadow-md flex items-center justify-center',
          'transition-transform duration-300 ease-in-out',
          isDark ? 'translate-x-6' : 'translate-x-0'
        )}
        style={{ left: '2px' }}
        aria-hidden="true"
      >
        <Sun
          className={cn(
            'absolute transition-all duration-200',
            'w-3 h-3',
            isDark ? 'opacity-0 scale-75' : 'opacity-100 scale-100 text-amber-500'
          )}
          aria-hidden="true"
        />
        <Moon
          className={cn(
            'absolute transition-all duration-200',
            'w-3 h-3',
            isDark ? 'opacity-100 scale-100 text-indigo-400' : 'opacity-0 scale-75'
          )}
          aria-hidden="true"
        />
      </span>
    </button>
  );
};
